import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";

interface SecurityVerificationProps {
  phone: string;
  email: string;
  googleAuthEnabled?: boolean;
  onVerify: (method: "sms" | "authenticator" | "email", code?: string) => Promise<void>;
  onRequestCode: (method: "sms" | "email") => Promise<void>;
  onConfirm: (smsCode: string, emailCode: string, authenticatorCode: string) => Promise<void>;
  isLoading?: boolean;
}

export const SecurityVerification: React.FC<SecurityVerificationProps> = ({
  phone,
  email,
  googleAuthEnabled = false,
  onVerify,
  onRequestCode,
  onConfirm,
  isLoading = false,
}) => {
  const [smsCode, setSmsCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [isRequestingCode, setIsRequestingCode] = useState<"sms" | "email" | null>(null);

  const handleRequestSmsCode = async () => {
    setIsRequestingCode("sms");
    try {
      await onRequestCode("sms");
    } finally {
      setIsRequestingCode(null);
    }
  };

  const handleRequestEmailCode = async () => {
    setIsRequestingCode("email");
    try {
      await onRequestCode("email");
    } finally {
      setIsRequestingCode(null);
    }
  };

  const handleConfirm = async () => {
    await onConfirm(smsCode, emailCode, authenticatorCode);
  };

  // Check if all required fields have values
  const isConfirmDisabled =
    !smsCode || !emailCode || (!authenticatorCode && googleAuthEnabled) || isLoading;

  const handlePasteAuthenticator = () => {
    navigator.clipboard
      .readText()
      .then((text) => {
        setAuthenticatorCode(text.trim());
      })
      .catch(() => {
        // Fallback - just focus the input for manual paste
        document.getElementById("authenticator-input")?.focus();
      });
  };

  return (
    <Card className="w-full max-w-md bg-white shadow-none border border-border-gray rounded-none">
      <CardHeader className="text-center space-y-2 pb-6 pt-8 px-4 sm:px-6">
        <CardTitle className="text-lg sm:text-xl font-bold uppercase tracking-wide">
          SECURITY VERIFICATION
          <br />
          REQUIREMENTS
        </CardTitle>
        <CardDescription className="text-sm text-gray-600">Confirmation steps:</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-4 sm:px-8 pb-8">
        {/* SMS Verification */}
        <div className="bg-disabled-bg flex items-center justify-between overflow-hidden">
          <input
            type="text"
            placeholder="SMS"
            value={smsCode}
            onChange={(e) => setSmsCode(e.target.value)}
            className="flex-1 min-w-0 border-0 bg-transparent h-14 text-sm sm:text-base font-normal focus:ring-0 focus:outline-none px-4 placeholder:text-gray-400 focus-visible:!outline-none"
            maxLength={6}
          />
          <Button
            variant="link"
            onClick={handleRequestSmsCode}
            disabled={isRequestingCode === "sms"}
            className="text-link-blue px-2 sm:px-4 h-auto text-sm sm:text-base font-normal hover:underline flex-shrink-0 whitespace-nowrap"
          >
            {isRequestingCode === "sms" ? "Sending..." : "Get code"}
          </Button>
        </div>

        {/* Email Verification */}
        <div className="bg-disabled-bg flex items-center justify-between overflow-hidden">
          <input
            type="text"
            placeholder="E-mail"
            value={emailCode}
            onChange={(e) => setEmailCode(e.target.value)}
            className="flex-1 min-w-0 border-0 bg-transparent h-14 text-sm sm:text-base font-normal focus:ring-0 focus:outline-none px-4 placeholder:text-gray-400 focus-visible:!outline-none"
            maxLength={6}
          />
          <Button
            variant="link"
            onClick={handleRequestEmailCode}
            disabled={isRequestingCode === "email"}
            className="text-link-blue px-2 sm:px-4 h-auto text-sm sm:text-base font-normal hover:underline flex-shrink-0 whitespace-nowrap"
          >
            {isRequestingCode === "email" ? "Sending..." : "Get code"}
          </Button>
        </div>

        {/* Google Authenticator */}
        {googleAuthEnabled && (
          <div className="bg-disabled-bg flex items-center justify-between overflow-hidden">
            <input
              id="authenticator-input"
              type="text"
              placeholder="Google-Authenticator code"
              value={authenticatorCode}
              onChange={(e) => setAuthenticatorCode(e.target.value)}
              className="flex-1 min-w-0 border-0 bg-transparent h-14 text-sm sm:text-base font-normal focus:ring-0 focus:outline-none px-4 placeholder:text-gray-400 placeholder:text-xs sm:placeholder:text-base focus-visible:!outline-none"
              maxLength={6}
            />
            <Button
              variant="link"
              onClick={handlePasteAuthenticator}
              className="text-link-blue px-2 sm:px-4 h-auto text-sm sm:text-base font-normal hover:underline flex-shrink-0 whitespace-nowrap"
            >
              Paste
            </Button>
          </div>
        )}

        <Button
          className="w-full h-12 bg-primary-blue hover:bg-primary-blue/90 text-white font-normal rounded-none mt-6"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        >
          {isLoading ? "Confirming..." : "Confirm"}
        </Button>
      </CardContent>
    </Card>
  );
};
